import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  getPublicProducts, 
  getPublicServices, 
  getPublicTrainings, 
  getPublicEvents, 
  getPublicPartners,
  getPublicTeam,
  getPublicWhyUs
} from '../services/api';
import toast from 'react-hot-toast';

const AllResources = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [showTabs, setShowTabs] = useState(true);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [data, setData] = useState({
    products: [],
    services: [],
    trainings: [],
    events: [],
    partners: [],
    team: [],
    whyus: []
  });
  const navigate = useNavigate();
  const location = useLocation();

  const tabs = [
    { id: 'products', label: 'Products', icon: 'fas fa-box', color: '#2C7A7B', gradient: 'linear-gradient(135deg, #2C7A7B 0%, #4A9D9E 100%)', count: data.products.length },
    { id: 'services', label: 'Services', icon: 'fas fa-cogs', color: '#3498db', gradient: 'linear-gradient(135deg, #3498db 0%, #5dade2 100%)', count: data.services.length },
    { id: 'trainings', label: 'Trainings', icon: 'fas fa-graduation-cap', color: '#F5A623', gradient: 'linear-gradient(135deg, #F5A623 0%, #f7b84d 100%)', count: data.trainings.length },
    { id: 'events', label: 'Events', icon: 'fas fa-calendar-alt', color: '#9b59b6', gradient: 'linear-gradient(135deg, #9b59b6 0%, #c39bd3 100%)', count: data.events.length },
    { id: 'partners', label: 'Partners', icon: 'fas fa-handshake', color: '#1abc9c', gradient: 'linear-gradient(135deg, #1abc9c 0%, #48c9b0 100%)', count: data.partners.length },
    { id: 'team', label: 'Team', icon: 'fas fa-users', color: '#e74c3c', gradient: 'linear-gradient(135deg, #e74c3c 0%, #f1948a 100%)', count: data.team.length },
    { id: 'whyus', label: 'Why Us', icon: 'fas fa-star', color: '#f39c12', gradient: 'linear-gradient(135deg, #f39c12 0%, #f7dc6f 100%)', count: data.whyus.length }
  ];

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
      setShowTabs(false);
      setSearchTerm('');
      setSelectedCategory('');
    } else {
      setShowTabs(true);
      setActiveTab('products');
    }
  }, [location.state]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [
        productsRes, servicesRes, trainingsRes, eventsRes, 
        partnersRes, teamRes, whyusRes
      ] = await Promise.all([
        getPublicProducts(),
        getPublicServices(),
        getPublicTrainings(),
        getPublicEvents(),
        getPublicPartners(),
        getPublicTeam(),
        getPublicWhyUs()
      ]);

      setData({
        products: extractData(productsRes),
        services: extractData(servicesRes),
        trainings: extractData(trainingsRes),
        events: extractData(eventsRes),
        partners: extractData(partnersRes),
        team: extractData(teamRes),
        whyus: extractData(whyusRes)
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading resources');
    } finally {
      setLoading(false);
    }
  };

  const extractData = (response) => {
    if (response && response.data && Array.isArray(response.data)) {
      return response.data.filter(item => item.isActive !== false);
    }
    if (response && Array.isArray(response)) {
      return response.filter(item => item.isActive !== false);
    }
    return [];
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  const getCurrentData = () => {
    let items = data[activeTab] || [];
    
    if (searchTerm) {
      items = items.filter(item => 
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedCategory) {
      items = items.filter(item => 
        item.category === selectedCategory || 
        item.type === selectedCategory ||
        item.department === selectedCategory
      );
    }
    
    return items;
  };

  const getCategories = () => {
    const items = data[activeTab] || [];
    const categories = new Set();
    items.forEach(item => {
      if (item.category) categories.add(item.category);
      if (item.type) categories.add(item.type);
      if (item.department) categories.add(item.department);
    });
    return Array.from(categories);
  };

  const handleViewDetails = (item) => {
    navigate(`/resource/${activeTab}/${item._id}`, { 
      state: { item, type: activeTab } 
    });
  };

  const getIcon = (tab) => {
    const icons = {
      products: '📦',
      services: '⚙️',
      trainings: '🎓',
      events: '📅',
      partners: '🤝',
      team: '👥',
      whyus: '⭐'
    };
    return icons[tab] || '📄';
  };

  const getTabColor = (tab) => {
    const tabItem = tabs.find(t => t.id === tab);
    return tabItem?.color || '#2C7A7B';
  };

  const getTabGradient = (tab) => {
    const tabItem = tabs.find(t => t.id === tab);
    return tabItem?.gradient || 'linear-gradient(135deg, #2C7A7B 0%, #4A9D9E 100%)';
  };

  const renderCard = (item, index) => {
    const imageUrl = item.imageUrl || item.logo || item.photo || item.avatar;
    const title = item.name || item.title;
    const description = item.description || item.bio || item.content;
    const category = item.category || item.type || item.department;
    const price = item.price;
    const tabGradient = getTabGradient(activeTab);
    const tabColor = getTabColor(activeTab);

    return (
      <div
        key={item._id || index}
        onClick={() => handleViewDetails(item)}
        style={{
          background: 'white',
          borderRadius: '24px',
          overflow: 'hidden',
          boxShadow: '0 20px 35px -10px rgba(0,0,0,0.1)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: 'pointer',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-8px)';
          e.currentTarget.style.boxShadow = '0 30px 45px -15px rgba(0,0,0,0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 20px 35px -10px rgba(0,0,0,0.1)';
        }}
      >
        {/* Image Section */}
        <div style={{
          position: 'relative',
          height: '240px',
          overflow: 'hidden',
          background: tabGradient
        }}>
          {imageUrl ? (
            <img 
              src={getFullImageUrl(imageUrl)} 
              alt={title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.5s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.08)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = `<div style="display: flex; align-items: center; justifyContent: center; height: 100%; font-size: 4rem; color: white;">${getIcon(activeTab)}</div>`;
              }}
            />
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              fontSize: '4rem',
              color: 'white'
            }}>
              {getIcon(activeTab)}
            </div>
          )}
          
          {/* Overlay Gradient */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '100px',
            background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)'
          }} />
          
          {/* Category Badge */}
          {category && (
            <div style={{
              position: 'absolute',
              top: '1rem',
              left: '1rem',
              background: 'rgba(0,0,0,0.75)',
              backdropFilter: 'blur(10px)',
              padding: '0.3rem 1rem',
              borderRadius: '30px',
              fontSize: '0.7rem',
              color: 'white',
              fontWeight: 600,
              letterSpacing: '0.5px',
              zIndex: 2
            }}>
              {category}
            </div>
          )}
          
          {/* Price Badge */}
          {price && (
            <div style={{
              position: 'absolute',
              bottom: '1rem',
              right: '1rem',
              background: 'rgba(0,0,0,0.75)',
              backdropFilter: 'blur(10px)',
              padding: '0.3rem 1rem',
              borderRadius: '30px',
              fontSize: '0.8rem',
              color: 'white',
              fontWeight: 700,
              zIndex: 2
            }}>
              {price}
            </div>
          )}
        </div>

        {/* Content Section */}
        <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
          <h3 style={{
            fontSize: '1.2rem',
            fontWeight: 700,
            color: '#0A192F',
            marginBottom: '0.5rem',
            lineHeight: '1.3',
            transition: 'color 0.2s'
          }}>
            {title}
          </h3>
          
          <p style={{
            color: '#5A6E7F',
            fontSize: '0.85rem',
            lineHeight: '1.6',
            marginBottom: '1.5rem',
            flex: 1
          }}>
            {description?.substring(0, 120)}
            {description?.length > 120 ? '...' : ''}
          </p>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(item);
            }}
            style={{
              width: '100%',
              padding: '0.8rem',
              background: tabGradient,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 4px 12px ${tabColor}60`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            View Details <i className="fas fa-arrow-right" style={{ marginLeft: '0.5rem', transition: 'transform 0.2s' }} />
          </button>
        </div>
      </div>
    );
  };

  const currentData = getCurrentData();
  const categories = getCategories();
  const activeTabConfig = tabs.find(t => t.id === activeTab);
  const currentTabName = activeTabConfig?.label || 'Products';
  const currentTabGradient = activeTabConfig?.gradient || 'linear-gradient(135deg, #2C7A7B 0%, #4A9D9E 100%)';

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F7F9FC' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loader" style={{
            width: '60px',
            height: '60px',
            border: '3px solid #E2E8F0',
            borderTopColor: '#2C7A7B',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }} />
          <p style={{ color: '#5A6E7F' }}>Loading resources...</p>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#F7F9FC', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Hero Banner */}
      <div style={{
        background: currentTabGradient,
        padding: '4rem 2rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          animation: 'rotate 20s linear infinite'
        }} />
        
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
          {/* Back to Home Button inside Hero */}
          <button
            onClick={() => navigate('/')}
            style={{
              position: 'absolute',
              top: '1rem',
              left: '1rem',
              padding: '0.5rem 1rem',
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.85rem',
              fontWeight: 500,
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
          >
            <i className="fas fa-home"></i> Home
          </button>
          
          <div style={{ textAlign: 'center' }}>
            <h1 style={{
              fontSize: 'clamp(2.5rem, 6vw, 4rem)',
              color: 'white',
              marginBottom: '1rem',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              textShadow: '0 2px 10px rgba(0,0,0,0.2)'
            }}>
              {!showTabs ? currentTabName : 'All Resources'}
            </h1>
            <p style={{
              fontSize: 'clamp(1rem, 3vw, 1.2rem)',
              color: 'rgba(255,255,255,0.95)',
              maxWidth: '600px',
              margin: '0 auto',
              opacity: 0.95
            }}>
              {!showTabs 
                ? `Discover our comprehensive collection of ${currentTabName.toLowerCase()}`
                : 'Explore our complete range of products, services, and solutions'
              }
            </p>
            <div style={{
              width: '60px',
              height: '4px',
              background: 'rgba(255,255,255,0.4)',
              margin: '1.5rem auto 0',
              borderRadius: '2px'
            }} />
          </div>
        </div>
        
        <style>{`
          @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>

      {/* Main Content */}
      <div className="container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem', flex: 1 }}>
        {/* Back Button when tabs are hidden */}
        {!showTabs && (
          <div style={{ marginBottom: '2rem' }}>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '0.5rem 1rem',
                background: 'white',
                color: '#2C7A7B',
                border: '1px solid #E2E8F0',
                borderRadius: '10px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.85rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#2C7A7B';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.borderColor = '#2C7A7B';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#2C7A7B';
                e.currentTarget.style.borderColor = '#E2E8F0';
              }}
            >
              <i className="fas fa-arrow-left"></i> Back
            </button>
          </div>
        )}

        {/* Tabs - Professional Pill Design */}
        {showTabs && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.75rem',
            justifyContent: 'center',
            marginBottom: '2rem',
            padding: '0.5rem',
            background: 'white',
            borderRadius: '60px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setSearchTerm('');
                  setSelectedCategory('');
                }}
                style={{
                  padding: '0.6rem 1.5rem',
                  background: activeTab === tab.id ? tab.gradient : 'transparent',
                  color: activeTab === tab.id ? 'white' : '#5A6E7F',
                  border: 'none',
                  borderRadius: '40px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.background = tab.gradient;
                    e.currentTarget.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#5A6E7F';
                  }
                }}
              >
                <i className={tab.icon}></i>
                {tab.label}
                <span style={{
                  background: activeTab === tab.id ? 'rgba(255,255,255,0.2)' : '#E2E8F0',
                  padding: '0.2rem 0.6rem',
                  borderRadius: '30px',
                  fontSize: '0.7rem',
                  marginLeft: '0.3rem'
                }}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Stats Bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '2rem',
          padding: '1rem 1.5rem',
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: `${getTabColor(activeTab)}15`,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className={activeTabConfig?.icon} style={{ color: getTabColor(activeTab), fontSize: '1.2rem' }}></i>
            </div>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0A192F' }}>{currentData.length}</div>
              <div style={{ fontSize: '0.8rem', color: '#5A6E7F' }}>{currentTabName} Available</div>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '1rem', flex: 1, justifyContent: 'flex-end' }}>
            <div style={{ position: 'relative', minWidth: '250px' }}>
              <i className="fas fa-search" style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#94A3B8'
              }} />
              <input
                type="text"
                placeholder={`Search ${currentTabName.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.6rem 1rem 0.6rem 2.5rem',
                  border: '1px solid #E2E8F0',
                  borderRadius: '12px',
                  fontSize: '0.9rem',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = getTabColor(activeTab)}
                onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
              />
            </div>
            
            {categories.length > 0 && (
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{
                  padding: '0.6rem 2rem 0.6rem 1rem',
                  border: '1px solid #E2E8F0',
                  borderRadius: '12px',
                  fontSize: '0.9rem',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = getTabColor(activeTab)}
                onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Results Grid */}
        {currentData.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem',
            background: 'white',
            borderRadius: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: '#F7F9FC',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <i className="fas fa-inbox" style={{ fontSize: '2rem', color: '#CBD5E1' }}></i>
            </div>
            <h3 style={{ color: '#0A192F', marginBottom: '0.5rem' }}>No {currentTabName.toLowerCase()} found</h3>
            <p style={{ color: '#5A6E7F', marginBottom: '1.5rem' }}>Try adjusting your search or filter criteria</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('');
              }}
              style={{
                padding: '0.6rem 1.5rem',
                background: currentTabGradient,
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '2rem'
          }}>
            {currentData.map((item, index) => renderCard(item, index))}
          </div>
        )}
      </div>

      {/* Professional Footer */}
      <footer style={{
        background: '#0A192F',
        color: '#A0B4C8',
        marginTop: '3rem',
        padding: '3rem 2rem 2rem'
      }}>
        <div className="container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem',
            marginBottom: '2rem'
          }}>
            {/* Company Info */}
            <div>
              <div style={{
                fontSize: '1.3rem',
                fontWeight: 700,
                color: 'white',
                marginBottom: '1rem'
              }}>
                INTELIHEADS <span style={{ color: '#2C7A7B' }}>INTERNET</span>
              </div>
              <p style={{
                fontSize: '0.85rem',
                lineHeight: '1.6',
                marginBottom: '1rem',
                color: '#A0B4C8'
              }}>
                Power System Analysis • Multiphysics Simulation • Model-Based Design
              </p>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <a href="#" style={{ color: '#A0B4C8', fontSize: '1.2rem', transition: 'color 0.2s' }}
                   onMouseEnter={(e) => e.target.style.color = '#2C7A7B'}
                   onMouseLeave={(e) => e.target.style.color = '#A0B4C8'}>
                  <i className="fab fa-linkedin"></i>
                </a>
                <a href="#" style={{ color: '#A0B4C8', fontSize: '1.2rem', transition: 'color 0.2s' }}
                   onMouseEnter={(e) => e.target.style.color = '#2C7A7B'}
                   onMouseLeave={(e) => e.target.style.color = '#A0B4C8'}>
                  <i className="fab fa-twitter"></i>
                </a>
                <a href="#" style={{ color: '#A0B4C8', fontSize: '1.2rem', transition: 'color 0.2s' }}
                   onMouseEnter={(e) => e.target.style.color = '#2C7A7B'}
                   onMouseLeave={(e) => e.target.style.color = '#A0B4C8'}>
                  <i className="fab fa-facebook"></i>
                </a>
                <a href="#" style={{ color: '#A0B4C8', fontSize: '1.2rem', transition: 'color 0.2s' }}
                   onMouseEnter={(e) => e.target.style.color = '#2C7A7B'}
                   onMouseLeave={(e) => e.target.style.color = '#A0B4C8'}>
                  <i className="fab fa-instagram"></i>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 style={{ color: 'white', marginBottom: '1rem', fontSize: '1rem' }}>Quick Links</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <button onClick={() => navigate('/')} style={{
                    background: 'none',
                    border: 'none',
                    color: '#A0B4C8',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#2C7A7B'}
                  onMouseLeave={(e) => e.target.style.color = '#A0B4C8'}>
                    Home
                  </button>
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <button onClick={() => navigate('/resources')} style={{
                    background: 'none',
                    border: 'none',
                    color: '#A0B4C8',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#2C7A7B'}
                  onMouseLeave={(e) => e.target.style.color = '#A0B4C8'}>
                    All Resources
                  </button>
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a href="#products" style={{
                    color: '#A0B4C8',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    transition: 'color 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#2C7A7B'}
                  onMouseLeave={(e) => e.target.style.color = '#A0B4C8'}>
                    Products
                  </a>
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a href="#services" style={{
                    color: '#A0B4C8',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#2C7A7B'}
                  onMouseLeave={(e) => e.target.style.color = '#A0B4C8'}>
                    Services
                  </a>
                </li>
                <li>
                  <a href="#contact" style={{
                    color: '#A0B4C8',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#2C7A7B'}
                  onMouseLeave={(e) => e.target.style.color = '#A0B4C8'}>
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 style={{ color: 'white', marginBottom: '1rem', fontSize: '1rem' }}>Resources</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a href="#" style={{
                    color: '#A0B4C8',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#2C7A7B'}
                  onMouseLeave={(e) => e.target.style.color = '#A0B4C8'}>
                    Training Catalog
                  </a>
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a href="#" style={{
                    color: '#A0B4C8',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#2C7A7B'}
                  onMouseLeave={(e) => e.target.style.color = '#A0B4C8'}>
                    Documentation
                  </a>
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a href="#" style={{
                    color: '#A0B4C8',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#2C7A7B'}
                  onMouseLeave={(e) => e.target.style.color = '#A0B4C8'}>
                    Case Studies
                  </a>
                </li>
                <li>
                  <a href="#" style={{
                    color: '#A0B4C8',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#2C7A7B'}
                  onMouseLeave={(e) => e.target.style.color = '#A0B4C8'}>
                    Blog
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 style={{ color: 'white', marginBottom: '1rem', fontSize: '1rem' }}>Contact Us</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <i className="fas fa-envelope" style={{ color: '#2C7A7B' }}></i>
                  <span style={{ fontSize: '0.85rem' }}>global@inteliheads.com</span>
                </li>
                <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <i className="fas fa-phone" style={{ color: '#2C7A7B' }}></i>
                  <span style={{ fontSize: '0.85rem' }}>+91-8076842489</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <i className="fas fa-map-marker-alt" style={{ color: '#2C7A7B' }}></i>
                  <span style={{ fontSize: '0.85rem' }}>Noida | Munich | Singapore</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div style={{
            textAlign: 'center',
            paddingTop: '2rem',
            borderTop: '1px solid rgba(160,180,200,0.1)',
            fontSize: '0.8rem'
          }}>
            <p>&copy; {new Date().getFullYear()} INTELIHEADS INTERNET Pvt. Ltd. All rights reserved.</p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.7rem' }}>
              Powering the future of engineering simulation and power systems
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AllResources;